import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuthContext } from "../../hooks/auth";
import { useClearAppCachesOnMount } from "../../hooks/useClearAppCachesOnMount";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faShieldAlt, faUser } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../components/ui/modal";
import { displaySuccess, displayError } from "../../utils/messageToast";
import { fetchUsers, setUserRole, type UserRow } from "../../functions/usersManagement";

export default function Users() {
  useClearAppCachesOnMount();
  const navigate = useNavigate();
  const { authorizationRA, authorizationForce, authorization, user } = useAuthContext();
  const isInForce = localStorage.getItem("inForce") === "true";
  const token = isInForce
    ? (authorizationForce || authorization || "")
    : (authorizationRA || authorization || "");

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/section/listForms", { replace: true });
    }
  }, [user, navigate]);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleModalLoading, setRoleModalLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [newRole, setNewRole] = useState<"user" | "admin">("user");

  const loadUsers = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const list = await fetchUsers(token);
      setUsers(list);
    } catch (e: any) {
      displayError(e?.message || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadUsers();
  }, [token]);

  const openRoleModal = (row: UserRow) => {
    setSelectedUser(row);
    setNewRole(row.role === "admin" ? "admin" : "user");
    setRoleModalOpen(true);
  };

  const handleSaveRole = async () => {
    if (!selectedUser) return;
    setRoleModalLoading(true);
    try {
      await setUserRole(token, {
        email: selectedUser.googleEmail,
        role: newRole,
      });
      displaySuccess("Role atualizado com sucesso.");
      setRoleModalOpen(false);
      await loadUsers();
    } catch (e: any) {
      displayError(e?.message || "Erro ao atualizar role.");
    } finally {
      setRoleModalLoading(false);
    }
  };

  const formatDate = (iso: string) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.googleEmail.toLowerCase().includes(q) ||
      (u.googleName || "").toLowerCase().includes(q) ||
      (u.clientName || "").toLowerCase().includes(q) ||
      u.slug.toLowerCase().includes(q)
    );
  });

  return (
    <div className="bg-page">
      <Helmet>
        <title>Usuários</title>
      </Helmet>

      <div className="bg-page-create-form-payload w-full px-2 sm:px-4 md:px-6 py-4 payload-page clients-management-page">
        <div className="form-page-shell">
          <div className="form-config-card clients-card">
            <div className="form-card-header clients-card-header">
              <div>
                <h2 className="form-card-title">Usuários</h2>
                <p className="form-card-subtitle">Visualize e gerencie os usuários que fizeram login na plataforma</p>
              </div>
              <input
                type="text"
                className="login-input"
                style={{ maxWidth: 280 }}
                placeholder="Buscar por nome, e-mail, cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="form-card-body clients-card-body">
              <div className="table-forms-container clients-table-wrap">
                {loading ? (
                  <div className="infoPage">
                    <FontAwesomeIcon icon={faSpinner} spin /> Carregando usuários…
                  </div>
                ) : (
                  <table className="list-forms-table clients-table">
                    <thead>
                      <tr>
                        <th>Usuário</th>
                        <th>E-mail</th>
                        <th>Cliente</th>
                        <th>Slug</th>
                        <th>Tipo</th>
                        <th>Último acesso</th>
                        <th className="clients-col-actions">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="clients-empty-cell">
                            {search ? "Nenhum usuário encontrado para a busca." : "Nenhum usuário cadastrado."}
                          </td>
                        </tr>
                      ) : (
                        filtered.map((row) => (
                          <tr key={row.id} className="bg-hover-item-list-forms">
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {row.googlePicture ? (
                                  <img
                                    src={row.googlePicture}
                                    alt={row.googleName || ""}
                                    referrerPolicy="no-referrer"
                                    style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0 }}
                                  />
                                ) : (
                                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <FontAwesomeIcon icon={faUser} style={{ fontSize: 12, color: "#6b7280" }} />
                                  </div>
                                )}
                                <span>{row.googleName || "—"}</span>
                              </div>
                            </td>
                            <td>{row.googleEmail}</td>
                            <td>{row.clientName || "—"}</td>
                            <td>{row.slug}</td>
                            <td>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  padding: "2px 8px",
                                  borderRadius: 99,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  background: row.role === "admin" ? "#ede9fe" : "#f3f4f6",
                                  color: row.role === "admin" ? "#7c3aed" : "#374151",
                                }}
                              >
                                {row.role === "admin" && <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: 10 }} />}
                                {row.role === "admin" ? "Admin" : "Usuário"}
                              </span>
                            </td>
                            <td>{formatDate(row.lastLoginAt)}</td>
                            <td>
                              <button
                                type="button"
                                className="list-action-icon"
                                onClick={() => openRoleModal(row)}
                                title="Alterar permissão"
                              >
                                <FontAwesomeIcon icon={faShieldAlt} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={roleModalOpen}
        title="Alterar permissão"
        onClose={() => !roleModalLoading && setRoleModalOpen(false)}
        onConfirm={handleSaveRole}
        confirmText="Salvar"
        cancelText="Cancelar"
        loading={roleModalLoading}
        hideActions={false}
      >
        {selectedUser && (
          <>
            <p style={{ margin: "0 0 12px", fontSize: 14, color: "#374151" }}>
              Usuário: <strong>{selectedUser.googleName || selectedUser.googleEmail}</strong>
            </p>
            <div className="form-info-row">
              <label className="form-info-label">Permissão</label>
              <select
                className="login-input"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as "user" | "admin")}
              >
                <option value="user">Usuário</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
